import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DialSliders, DialChips, DEFAULT_DIALS } from "@/components/DialSliders";
import { ResumeSessions } from "@/components/ResumeSessions";
import { RoleplayStory } from "@/components/RoleplayStory";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { SlidersHorizontal, DoorOpen, Phone, PhoneCall, Sofa, Store, Swords, Shuffle, HeartHandshake } from "lucide-react";

export const EPIC = "bg-white rounded-3xl border border-slate-200/80 shadow-[0_4px_20px_-4px_rgba(15,23,42,0.06)]";

const DIFF = {
  Easy: "text-emerald-600 bg-emerald-500/10 border-emerald-500/30",
  Medium: "text-cyan-600 bg-cyan-500/10 border-cyan-500/30",
  Hard: "text-amber-600 bg-amber-500/10 border-amber-500/30",
  Elite: "text-red-600 bg-red-500/10 border-red-500/30",
};

function CustomiseDialog({ persona, onClose, onStart }) {
  const [dials, setDials] = useState(DEFAULT_DIALS);
  useEffect(() => {
    if (persona) setDials({ objection_level: persona.objection_level ?? 5, budget: persona.budget ?? 5, attitude: persona.attitude ?? 5 });
  }, [persona]);
  return (
    <Dialog open={!!persona} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white border-slate-200 text-slate-900" data-testid="customise-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            {persona && <img src={persona.avatar} alt="" className="w-10 h-10 rounded-full object-cover" />}
            Customise {persona?.name}
          </DialogTitle>
          <DialogDescription>Tune this customer for one session. Your scores still count.</DialogDescription>
        </DialogHeader>
        <DialSliders values={dials} onChange={setDials} idPrefix="custom-dial" />
        <DialogFooter>
          <Button onClick={() => onStart(persona, dials)} className="w-full bg-emerald-500 hover:bg-emerald-600 text-black font-semibold h-11" data-testid="customise-start-btn">
            <SlidersHorizontal className="w-4 h-4 mr-2" /> Start custom roleplay
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const CHANNEL_ICONS = { door_to_door: DoorOpen, phone_cold: Phone, phone_followup: PhoneCall, in_home: Sofa, retail: Store };
const ChannelIcon = ({ id, className }) => { const I = CHANNEL_ICONS[id] || Store; return <I className={className} />; };

export function ChannelPicker({ channels, value, onChange, compact = false }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid="channel-picker">
      {channels.map((c) => {
        const Icon = CHANNEL_ICONS[c.id] || Store;
        const active = c.id === value;
        return (
          <button key={c.id} onClick={() => onChange(c.id)} type="button"
            className={`inline-flex items-center gap-1.5 rounded-full border font-medium transition-colors ${compact ? "px-2.5 py-1 text-[11px]" : "px-3.5 py-2 text-sm"} ${active ? "bg-emerald-500 text-black border-emerald-500 shadow-sm" : "bg-white text-slate-700 border-slate-300 hover:border-emerald-400 hover:text-emerald-700"}`}
            aria-pressed={active} data-testid={`channel-${c.id}`}>
            <Icon className={compact ? "w-3 h-3" : "w-4 h-4"} /> {compact ? c.short : c.label}
          </button>
        );
      })}
    </div>
  );
}

export function LanguagePicker({ languages, value, onChange }) {
  if (!languages.length) return null;
  return (
    <div className="flex flex-wrap gap-2" data-testid="language-picker">
      {languages.map((l) => {
        const active = l.id === value;
        return (
          <button key={l.id} onClick={() => onChange(l.id)} type="button"
            className={`inline-flex items-center gap-1.5 rounded-full border font-medium transition-colors px-3 py-1.5 text-sm ${active ? "bg-slate-900 text-white border-slate-900 shadow-sm" : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"}`}
            aria-pressed={active} data-testid={`language-${l.id}`}>
            <span aria-hidden>{l.flag}</span> {l.label}{l.id !== "en" && <span className="text-[11px] opacity-70">· {l.native}</span>}
          </button>
        );
      })}
    </div>
  );
}

const DIFF_ORDER = ["All", "Easy", "Medium", "Hard", "Elite"];

export function PersonaGrid({ personas, onSelect, onResume, starting, channels, channel, setChannel, primaryChannel, product, setProduct, languages, language, setLanguage }) {
  const [customising, setCustomising] = useState(null);
  const [filter, setFilter] = useState("All");
  const current = channels.find((c) => c.id === channel);
  const currentLang = languages.find((l) => l.id === language);
  const shown = personas.filter((p) => filter === "All" || p.difficulty === filter);
  return (
    <motion.div initial="hidden" animate="show" variants={{ hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.06 } } }} className="space-y-6">
      <motion.div variants={{ hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0 } }} className={`${EPIC} relative overflow-hidden p-6 sm:p-8`} data-testid="roleplay-hero">
        <div className="absolute -top-24 -right-20 w-72 h-72 rounded-full bg-emerald-400/15 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-28 left-1/4 w-80 h-80 rounded-full bg-cyan-400/10 blur-3xl pointer-events-none" />
        <div className="relative flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-emerald-700">Roleplay arena</p>
            <h1 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-extrabold tracking-tight text-slate-900 mt-1 leading-[1.08]">Choose your <span className="text-emerald-600">customer</span></h1>
            <p className="text-slate-600 mt-2 max-w-xl">Every conversation is different. Pick who you want to practise against today — or tune their personality first.</p>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="setup-summary">
            {current && <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white text-xs font-semibold px-3 py-1.5"><ChannelIcon id={current.id} className="w-3.5 h-3.5" /> {current.label}</span>}
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-200 text-slate-700 text-xs font-semibold px-3 py-1.5"><Store className="w-3.5 h-3.5 text-emerald-600" /> {product.trim() ? product.trim().slice(0, 36) : "General pitch"}</span>
            {currentLang && <span className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-200 text-slate-700 text-xs font-semibold px-3 py-1.5">{currentLang.flag} {currentLang.label}</span>}
          </div>
        </div>
      </motion.div>

      <ResumeSessions onResume={(s) => onResume(s.id)} />

      <RoleplayStory onStart={() => document.querySelector("[data-testid=persona-grid]")?.scrollIntoView({ behavior: "smooth", block: "start" })} />

      <motion.div variants={{ hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0 } }} className={`${EPIC} p-5 sm:p-6 grid lg:grid-cols-3 gap-6`} data-testid="channel-card">
        <div>
          <div className="flex items-center justify-between gap-2">
            <div className="text-xs font-bold uppercase tracking-widest text-emerald-700">1 · Sales channel</div>
            {primaryChannel && channel !== primaryChannel && (
              <button onClick={() => setChannel(primaryChannel)} className="text-xs text-slate-500 hover:text-emerald-700 underline-offset-2 hover:underline" data-testid="channel-reset-btn">Back to my default</button>
            )}
          </div>
          <p className="text-sm text-slate-600 mt-1">Where is this happening? The customer reacts to the setting.</p>
          <div className="mt-3"><ChannelPicker channels={channels} value={channel} onChange={setChannel} /></div>
          {current && <p className="text-xs text-slate-500 mt-3" data-testid="channel-current">Practising <span className="font-semibold text-slate-700">{current.label.toLowerCase()}</span>{primaryChannel === channel ? " · your default" : ""}.</p>}
        </div>
        <div className="lg:border-l lg:border-slate-200/80 lg:pl-6">
          <label className="text-xs font-bold uppercase tracking-widest text-emerald-700" htmlFor="product-input">2 · What are you selling?</label>
          <p className="text-sm text-slate-600 mt-1">Their questions, objections and prices will be about <em>your</em> product.</p>
          <Input id="product-input" value={product} onChange={(e) => setProduct(e.target.value)} maxLength={200}
            placeholder="e.g. Solar panels with battery storage"
            className="mt-3 bg-slate-50 border-slate-200 text-slate-900 h-11 rounded-xl" data-testid="product-input" />
          <p className="text-[11px] text-slate-400 mt-1.5" data-testid="product-hint">{product.trim() ? "Saved as your default product for future roleplays." : "Leave blank and the customer will react to a general pitch."}</p>
        </div>
        <div className="lg:border-l lg:border-slate-200/80 lg:pl-6">
          <div className="text-xs font-bold uppercase tracking-widest text-emerald-700">3 · Conversation language</div>
          <p className="text-sm text-slate-600 mt-1">The customer speaks and listens in it; your scorecard follows too.</p>
          <div className="mt-3"><LanguagePicker languages={languages} value={language} onChange={setLanguage} /></div>
          {currentLang && currentLang.id !== "en" && <p className="text-xs text-slate-500 mt-2" data-testid="language-current">Customer will speak <span className="font-semibold text-slate-700">{currentLang.label}</span>. Regional UK accents are switched off for this language.</p>}
        </div>
      </motion.div>

      <motion.div variants={{ hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0 } }} className="flex items-center justify-between gap-3 flex-wrap scroll-mt-4" data-testid="persona-grid">
        <h2 className="text-base md:text-lg font-bold tracking-tight text-slate-900 flex items-center gap-2"><Swords className="w-5 h-5 text-emerald-600" /> {shown.length} customers ready</h2>
        <div className="flex gap-1.5 rounded-full bg-white border border-slate-200 p-1" data-testid="difficulty-filter">
          {DIFF_ORDER.map((d) => (
            <button key={d} onClick={() => setFilter(d)} className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${filter === d ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"}`} data-testid={`difficulty-${d.toLowerCase()}`}>{d}</button>
          ))}
        </div>
      </motion.div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {shown.map((p, i) => (
          <motion.div key={p.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
            whileHover={{ y: -5 }} role="button" tabIndex={0} aria-disabled={starting}
            onClick={() => !starting && onSelect(p)} onKeyDown={(e) => e.key === "Enter" && !starting && onSelect(p)}
            className={`text-left ${EPIC} overflow-hidden hover:shadow-[0_18px_40px_-12px_rgba(15,23,42,0.18)] hover:border-emerald-300 transition-all duration-300 group cursor-pointer ${starting ? "opacity-50 pointer-events-none" : ""}`}
            data-testid={`persona-${p.id}`}>
            <div className="relative h-48">
              <img src={p.avatar} alt={p.name} className="w-full h-full object-cover object-top group-hover:scale-[1.03] transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-900/10 to-transparent" />
              <span className={`absolute top-3 right-3 px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-wider border backdrop-blur bg-white/85 ${DIFF[p.difficulty]}`}>{p.difficulty}</span>
              {p.barge_in > 0 && <span className="absolute top-3 left-3 px-2 py-1 rounded-full text-[10px] font-semibold bg-red-500 text-white shadow">Interrupts</span>}
              {p.scenario === "vulnerable"
                ? <span className={`absolute ${p.barge_in > 0 ? "top-10" : "top-3"} left-3 px-2 py-1 rounded-full text-[10px] font-semibold bg-teal-600 text-white shadow inline-flex items-center gap-1`} data-testid={`responsible-badge-${p.id}`}><HeartHandshake className="w-3 h-3" /> Responsible selling</span>
                : p.random && <span className={`absolute ${p.barge_in > 0 ? "top-10" : "top-3"} left-3 px-2 py-1 rounded-full text-[10px] font-semibold bg-violet-600 text-white shadow inline-flex items-center gap-1`} data-testid={`random-badge-${p.id}`}><Shuffle className="w-3 h-3" /> Random every time</span>}
              <div className="absolute bottom-3 left-4 right-4 text-white">
                <div className="font-extrabold text-lg leading-tight font-display tracking-tight">{p.title}</div>
                <div className="text-xs text-white/80">{p.name}</div>
              </div>
            </div>
            <div className="p-4">
              <p className="text-sm text-slate-600 leading-snug min-h-[2.5rem]">{p.tagline}</p>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {p.traits.map((t) => <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200/70">{t}</span>)}
              </div>
              <DialChips persona={p} />
              <div className="flex gap-2 mt-4">
                <Button onClick={(e) => { e.stopPropagation(); !starting && onSelect(p); }} size="sm"
                  className="flex-1 h-10 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold shadow-[0_4px_14px_rgba(16,185,129,0.35)]" data-testid={`start-persona-${p.id}`}>
                  <Swords className="w-4 h-4 mr-1.5" /> Start
                </Button>
                <Button onClick={(e) => { e.stopPropagation(); setCustomising(p); }} size="sm" variant="outline"
                  className="h-10 rounded-xl border-slate-300 bg-white text-slate-800 hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-300"
                  title="Adjust objection level, budget and attitude" data-testid={`customise-persona-${p.id}`}>
                  <SlidersHorizontal className="w-4 h-4 mr-1.5" /> Tune
                </Button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
      <CustomiseDialog persona={customising} onClose={() => setCustomising(null)}
        onStart={(p, dials) => { setCustomising(null); onSelect(p, dials); }} />
    </motion.div>
  );
}
